"use client"
import { useEffect, useRef } from "react"
import RadarChart from "./RadarChart"
import { calculateScores } from "../utils/scoreCalculator"

const ThankYouPage = ({ userData, assessmentData, onReset }) => {
  const scores = calculateScores(assessmentData.answers)
  const chartRef = useRef(null)
  const chartInstance = useRef(null)

  // Calculate overall level and average score
  const scoreValues = Object.values(scores)
  const averageScore = scoreValues.reduce((sum, score) => sum + score, 0) / scoreValues.length
  const level = Math.min(Math.floor(averageScore / 20) + 1, 5) // Levels 1-5 based on 0-100 scale

  const getScoreColor = (score) => {
    if (score >= 67) return "text-green-600"
    if (score >= 34) return "text-yellow-600"
    return "text-red-600"
  }

  const getScoreLabel = (score) => {
    if (score >= 67) return "Strong"
    if (score >= 34) return "Moderate"
    return "Needs Improvement"
  }

  const getScoreBadge = (score) => {
    if (score >= 67) return "bg-green-100 text-green-800"
    if (score >= 34) return "bg-yellow-100 text-yellow-800"
    return "bg-red-100 text-red-800"
  }

  const getLevelBadge = () => {
    if (level >= 4) return "bg-green-100 text-green-800"
    if (level >= 3) return "bg-yellow-100 text-yellow-800"
    return "bg-red-100 text-red-800"
  }

  const getLevelLabel = () => {
    if (level === 5) return "Excellent"
    if (level === 4) return "Strong"
    if (level === 3) return "Moderate"
    if (level === 2) return "Fair"
    return "Needs Improvement"
  }

  useEffect(() => {
    if (chartRef.current && chartInstance.current) {
      chartInstance.current.destroy()
    }
  }, [scores])

  return (
    <div className="min-h-screen bg-gradient-to-br from-cyan-50 via-white to-blue-50 relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/subtle-white-feathers.png')] opacity-20 z-0"></div>

      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <h1 className="text-2xl md:text-3xl font-bold text-gray-900 animate-fade-in">GTM Assessment</h1>
            <div className="flex items-center space-x-4 md:space-x-6">
              <div className="flex items-center space-x-2">
                <div className="w-8 h-8 bg-green-500 text-white rounded-full flex items-center justify-center text-sm font-semibold">
                  ✓
                </div>
                <span className="text-sm font-medium text-green-600 hidden sm:block">Registration</span>
              </div>
              <div className="flex-1 h-0.5 bg-gray-200"></div>
              <div className="flex items-center space-x-2">
                <div className="w-8 h-8 bg-green-500 text-white rounded-full flex items-center justify-center text-sm font-semibold">
                  ✓
                </div>
                <span className="text-sm font-medium text-green-600 hidden sm:block">Assessment</span>
              </div>
              <div className="flex-1 h-0.5 bg-cyan-200"></div>
              <div className="flex items-center space-x-2">
                <div className="w-8 h-8 bg-cyan-600 text-white rounded-full flex items-center justify-center text-sm font-semibold animate-pulse">
                  3
                </div>
                <span className="text-sm font-medium text-cyan-600 hidden sm:block">Results</span>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12 relative z-10">
        {/* Thank You Message */}
        <div className="text-center mb-12 animate-fade-in-up">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <svg className="w-10 h-10 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
          </div>
          <h2 className="text-3xl md:text-5xl font-bold text-gray-900 mb-4">Congratulations, {userData.name}!</h2>
          <p className="text-lg md:text-xl text-gray-600 max-w-3xl mx-auto mb-8">
            Your GTM assessment is complete. Discover your personalized insights and unlock the full potential of your go-to-market strategy.
          </p>
          <div className="inline-flex items-center justify-center bg-cyan-600 text-white rounded-full px-6 py-3 mb-8">
            <span className="text-lg font-semibold">Overall Level: {level} - {getLevelLabel(level)}</span>
          </div>
        </div>

        {/* Radar Chart Section */}
        <div className="card bg-white rounded-2xl shadow-xl p-8 mb-12 animate-fade-in-up">
          <div className="flex items-center justify-between mb-8">
            <h3 className="text-2xl md:text-3xl font-semibold text-gray-900">Your GTM Assessment Report</h3>
            <p className="text-sm text-gray-500">Average Score: {Math.round(averageScore)} / 100</p>
          </div>
          <div className="max-w-3xl mx-auto relative">
            <canvas ref={chartRef} className="w-full h-80" />
            <RadarChart scores={scores} canvasRef={chartRef} />
          </div>
          <p className="text-center text-gray-600 mt-6">
            This radar chart visualizes your performance across six key GTM dimensions. Use these insights to drive strategic improvements.
          </p>
        </div>

        {/* Detailed Scores */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
          {Object.entries(scores).map(([dimension, score]) => (
            <div key={dimension} className="card bg-white rounded-xl shadow-md p-6 hover:shadow-lg transition-all duration-300">
              <div className="flex items-center justify-between mb-4">
                <h4 className="text-lg font-semibold text-gray-900 capitalize">
                  {dimension.replace(/([A-Z])/g, " $1").trim()}
                </h4>
                <span className={`px-3 py-1 rounded-full text-sm font-medium ${getScoreBadge(score)}`}>
                  {getScoreLabel(score)}
                </span>
              </div>
              <div className="flex items-center">
                <div className="flex-1 mr-4">
                  <div className="h-3 bg-gray-200 rounded-full overflow-hidden">
                    <div
                      className={`h-full rounded-full transition-all duration-1000 ${
                        score >= 67 ? "bg-green-500" : score >= 34 ? "bg-yellow-500" : "bg-red-500"
                      }`}
                      style={{ width: `${score}%` }}
                    ></div>
                  </div>
                </div>
                <span className={`text-2xl font-bold ${getScoreColor(score)}`}>{Math.round(score)}</span>
              </div>
              <p className="text-sm text-gray-600 mt-3">
                {score >= 67 ? "Excellent foundation - continue to optimize" : score >= 34 ? "Solid base - focus on targeted improvements" : "Priority area - strategic overhaul recommended"}
              </p>
            </div>
          ))}
        </div>

        {/* Insights Section */}
        <div className="bg-white rounded-2xl shadow-lg p-8 mb-12 animate-fade-in-up animation-delay-200">
          <h3 className="text-2xl font-bold text-gray-900 mb-6 text-center">Actionable Insights</h3>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-cyan-50 rounded-lg p-6">
              <h4 className="text-lg font-semibold text-cyan-800 mb-3">Strengths</h4>
              <ul className="space-y-2 text-sm text-gray-700">
                {scoreValues
                  .map((score, index) => ({ score, dimension: Object.keys(scores)[index] }))
                  .filter(item => item.score >= 67)
                  .slice(0, 3)
                  .map(item => (
                    <li key={item.dimension} className="flex items-center">
                      <svg className="w-4 h-4 text-green-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                      {item.dimension.replace(/([A-Z])/g, " $1").trim()}
                    </li>
                  ))}
              </ul>
            </div>
            <div className="bg-yellow-50 rounded-lg p-6">
              <h4 className="text-lg font-semibold text-yellow-800 mb-3">Opportunities</h4>
              <ul className="space-y-2 text-sm text-gray-700">
                {scoreValues
                  .map((score, index) => ({ score, dimension: Object.keys(scores)[index] }))
                  .filter(item => item.score < 67)
                  .slice(0, 3)
                  .map(item => (
                    <li key={item.dimension} className="flex items-center">
                      <svg className="w-4 h-4 text-yellow-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                      </svg>
                      {item.dimension.replace(/([A-Z])/g, " $1").trim()}
                    </li>
                  ))}
              </ul>
            </div>
          </div>
          <div className="text-center mt-6">
            <p className="text-gray-600 mb-4">Ready to turn these insights into action? Our experts can help you implement a winning GTM strategy.</p>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-12">
          <button
            onClick={onReset}
            className="px-8 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 hover:scale-105 transition-all duration-200"
          >
            ← Back to Homepage
          </button>
          <a
            href="https://calendly.com/your-calendar-link"
            target="_blank"
            rel="noopener noreferrer"
            className="px-8 py-3 bg-gradient-to-r from-cyan-600 to-blue-600 text-white font-semibold rounded-lg shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-200"
          >
            Book Free Consultation →
          </a>
        </div>

        {/* Company Info */}
        <div className="text-center bg-white rounded-2xl shadow-lg p-8 animate-fade-in-up animation-delay-400">
          <h4 className="text-xl font-semibold text-gray-900 mb-4">Assessment Summary</h4>
          <div className="grid md:grid-cols-2 gap-4 text-sm text-gray-600 max-w-md mx-auto">
            <div className="flex justify-between">
              <span><strong>Company:</strong></span>
              <span>{userData.company}</span>
            </div>
            <div className="flex justify-between">
              <span><strong>Role:</strong></span>
              <span>{userData.role}</span>
            </div>
            <div className="flex justify-between">
              <span><strong>Company Size:</strong></span>
              <span>{userData.companySize}</span>
            </div>
            <div className="flex justify-between">
              <span><strong>Assessment Date:</strong></span>
              <span>{new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</span>
            </div>
            <div className="flex justify-between pt-2 border-t border-gray-200">
              <span><strong>Overall Score:</strong></span>
              <span className={`font-bold ${getScoreColor(averageScore)}`}>{Math.round(averageScore)} / 100</span>
            </div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-gray-900 text-white text-center py-6 mt-12">
        <p className="text-sm">&copy; 2025 GTM Assessment. All Rights Reserved. | <a href="#" className="underline">Privacy</a> | <a href="#" className="underline">Terms</a></p>
      </footer>
    </div>
  )
}

export default ThankYouPage